import React, { Suspense, lazy, useContext } from 'react';

import { ExternalPatientContext } from '@/app/external/constants';
import type { ValidUniversalToothId } from '@videa/utils/src/normalizeToothIds';

import type { PreferencesResponse } from '../../../../@types/preferences';
import { usePreferences } from '../../../../react-query/usePreferences';
import type { TProcedureStatus } from '../../../../react-query/useVisitSummaries';
import Tooth10Normal from '../../../../svgs/odontogram-svgs/components/Tooth10Normal';
import Tooth11Normal from '../../../../svgs/odontogram-svgs/components/Tooth11Normal';
import Tooth12Normal from '../../../../svgs/odontogram-svgs/components/Tooth12Normal';
import Tooth13Normal from '../../../../svgs/odontogram-svgs/components/Tooth13Normal';
import Tooth14Normal from '../../../../svgs/odontogram-svgs/components/Tooth14Normal';
import Tooth15Normal from '../../../../svgs/odontogram-svgs/components/Tooth15Normal';
import Tooth16Normal from '../../../../svgs/odontogram-svgs/components/Tooth16Normal';
import Tooth17Normal from '../../../../svgs/odontogram-svgs/components/Tooth17Normal';
import Tooth18Normal from '../../../../svgs/odontogram-svgs/components/Tooth18Normal';
import Tooth19Normal from '../../../../svgs/odontogram-svgs/components/Tooth19Normal';
import Tooth1Normal from '../../../../svgs/odontogram-svgs/components/Tooth1Normal';
import Tooth20Normal from '../../../../svgs/odontogram-svgs/components/Tooth20Normal';
import Tooth21Normal from '../../../../svgs/odontogram-svgs/components/Tooth21Normal';
import Tooth22Normal from '../../../../svgs/odontogram-svgs/components/Tooth22Normal';
import Tooth23Normal from '../../../../svgs/odontogram-svgs/components/Tooth23Normal';
import Tooth24Normal from '../../../../svgs/odontogram-svgs/components/Tooth24Normal';
import Tooth25Normal from '../../../../svgs/odontogram-svgs/components/Tooth25Normal';
import Tooth26Normal from '../../../../svgs/odontogram-svgs/components/Tooth26Normal';
import Tooth27Normal from '../../../../svgs/odontogram-svgs/components/Tooth27Normal';
import Tooth28Normal from '../../../../svgs/odontogram-svgs/components/Tooth28Normal';
import Tooth29Normal from '../../../../svgs/odontogram-svgs/components/Tooth29Normal';
import Tooth2Normal from '../../../../svgs/odontogram-svgs/components/Tooth2Normal';
import Tooth30Normal from '../../../../svgs/odontogram-svgs/components/Tooth30Normal';
import Tooth31Normal from '../../../../svgs/odontogram-svgs/components/Tooth31Normal';
import Tooth32Normal from '../../../../svgs/odontogram-svgs/components/Tooth32Normal';
import Tooth3Normal from '../../../../svgs/odontogram-svgs/components/Tooth3Normal';
import Tooth4Normal from '../../../../svgs/odontogram-svgs/components/Tooth4Normal';
import Tooth5Normal from '../../../../svgs/odontogram-svgs/components/Tooth5Normal';
import Tooth6Missing from '../../../../svgs/odontogram-svgs/components/Tooth6Missing';
import Tooth6Normal from '../../../../svgs/odontogram-svgs/components/Tooth6Normal';
import Tooth7Normal from '../../../../svgs/odontogram-svgs/components/Tooth7Normal';
import Tooth8Normal from '../../../../svgs/odontogram-svgs/components/Tooth8Normal';
import Tooth9Normal from '../../../../svgs/odontogram-svgs/components/Tooth9Normal';
import ToothANormal from '../../../../svgs/odontogram-svgs/components/ToothANormal';
import ToothBNormal from '../../../../svgs/odontogram-svgs/components/ToothBNormal';
import ToothCNormal from '../../../../svgs/odontogram-svgs/components/ToothCNormal';
import ToothDNormal from '../../../../svgs/odontogram-svgs/components/ToothDNormal';
import ToothENormal from '../../../../svgs/odontogram-svgs/components/ToothENormal';
import ToothFNormal from '../../../../svgs/odontogram-svgs/components/ToothFNormal';
import ToothGNormal from '../../../../svgs/odontogram-svgs/components/ToothGNormal';
import ToothHNormal from '../../../../svgs/odontogram-svgs/components/ToothHNormal';
import ToothINormal from '../../../../svgs/odontogram-svgs/components/ToothINormal';
import ToothJNormal from '../../../../svgs/odontogram-svgs/components/ToothJNormal';
import ToothKNormal from '../../../../svgs/odontogram-svgs/components/ToothKNormal';
import ToothLNormal from '../../../../svgs/odontogram-svgs/components/ToothLNormal';
import ToothMNormal from '../../../../svgs/odontogram-svgs/components/ToothMNormal';
import ToothNNormal from '../../../../svgs/odontogram-svgs/components/ToothNNormal';
import ToothONormal from '../../../../svgs/odontogram-svgs/components/ToothONormal';
import ToothPNormal from '../../../../svgs/odontogram-svgs/components/ToothPNormal';
import ToothQNormal from '../../../../svgs/odontogram-svgs/components/ToothQNormal';
import ToothRNormal from '../../../../svgs/odontogram-svgs/components/ToothRNormal';
import ToothSNormal from '../../../../svgs/odontogram-svgs/components/ToothSNormal';
import ToothTNormal from '../../../../svgs/odontogram-svgs/components/ToothTNormal';

import { getHexFromProcedureStatus } from './constants';
import { useIsBasicOdontogram } from './hooks';
import type { ToothSvgComponentProps } from './types';

// TODO: Change this to use the ToothProcedureNames enum (in addition to the
// other supported visualizations) once we support the "other" procedure name
export const supportedVisualizations = [
  'crown',
  'enamelRegen',
  'extracted',
  'extraction',
  'filling',
  'implant',
  'missing',
  'normal',
  'rootAndCrown',
  'rootCanal',
  'unknown',
] as const;

export type TSupportedVisualizations = (typeof supportedVisualizations)[number];

export function isSupportedVisualization(
  name?: string,
): name is TSupportedVisualizations {
  return (
    !!name && supportedVisualizations.includes(name as TSupportedVisualizations)
  );
}

export type ToothSvgComponent = React.LazyExoticComponent<
  React.FC<ToothSvgComponentProps>
>;

export interface ToothStatus {
  type: TSupportedVisualizations;
  toothId: ValidUniversalToothId;
  hx?: number;
  wx?: number;
  status?: TProcedureStatus;
}

interface ToothProps extends ToothStatus {
  isDarkMode?: boolean;
}

const toothComponentCache: Record<string, ToothSvgComponent> = {};

const defaultTeethMap = {
  '1': Tooth1Normal,
  '2': Tooth2Normal,
  '3': Tooth3Normal,
  '4': Tooth4Normal,
  '5': Tooth5Normal,
  '6': Tooth6Normal,
  '7': Tooth7Normal,
  '8': Tooth8Normal,
  '9': Tooth9Normal,
  '10': Tooth10Normal,
  '11': Tooth11Normal,
  '12': Tooth12Normal,
  '13': Tooth13Normal,
  '14': Tooth14Normal,
  '15': Tooth15Normal,
  '16': Tooth16Normal,
  '17': Tooth17Normal,
  '18': Tooth18Normal,
  '19': Tooth19Normal,
  '20': Tooth20Normal,
  '21': Tooth21Normal,
  '22': Tooth22Normal,
  '23': Tooth23Normal,
  '24': Tooth24Normal,
  '25': Tooth25Normal,
  '26': Tooth26Normal,
  '27': Tooth27Normal,
  '28': Tooth28Normal,
  '29': Tooth29Normal,
  '30': Tooth30Normal,
  '31': Tooth31Normal,
  '32': Tooth32Normal,
  A: ToothANormal,
  B: ToothBNormal,
  C: ToothCNormal,
  D: ToothDNormal,
  E: ToothENormal,
  F: ToothFNormal,
  G: ToothGNormal,
  H: ToothHNormal,
  I: ToothINormal,
  J: ToothJNormal,
  K: ToothKNormal,
  L: ToothLNormal,
  M: ToothMNormal,
  N: ToothNNormal,
  O: ToothONormal,
  P: ToothPNormal,
  Q: ToothQNormal,
  R: ToothRNormal,
  S: ToothSNormal,
  T: ToothTNormal,
};

const getToothComponent = (
  toothId: ValidUniversalToothId,
  type: TSupportedVisualizations | 'other',
  prefs?: PreferencesResponse,
): ToothSvgComponent => {
  const key = `${toothId}${type}`;
  // Default to normal if the procedure name is not supported
  if (!isSupportedVisualization(type) && prefs?.showUnknownViz) {
    type = 'unknown';
  } else if (!isSupportedVisualization(type)) {
    type = 'normal';
  }

  if (!toothComponentCache[key]) {
    toothComponentCache[key] = lazy(() =>
      import(
        `../../../../svgs/odontogram-svgs/components/Tooth${toothId}${
          type === 'enamelRegen'
            ? 'Incipient'
            : type.charAt(0).toUpperCase() + type.slice(1)
        }.tsx`
      ).catch((err) => {
        console.error('Error loading SVG:', err);
        return { default: defaultTeethMap[toothId] }; // Fallback component
      }),
    );
  }
  return toothComponentCache[key];
};

export const Tooth: React.FC<ToothProps> = React.memo(
  ({ type, status, toothId, hx, wx, isDarkMode }) => {
    const { isIframe } = useContext(ExternalPatientContext);
    const { data: prefs } = usePreferences();
    const isBasicOdontogram = useIsBasicOdontogram();

    const SvgComponent = getToothComponent(toothId, type, prefs);

    let findingsFill = '';
    if (status) {
      findingsFill = getHexFromProcedureStatus(status, !!isDarkMode) ?? '';
    } else if (type === 'missing') {
      findingsFill = 'transparent';
    }

    let fill = type === 'missing' ? '#FAFAFA' : '#E4E4E7';
    if (isDarkMode) {
      fill = type === 'missing' ? 'transparent' : '#94a3b8';
    }

    let stroke = type === 'missing' ? '#E4E4E7' : '#A1A1AA';
    if (isDarkMode) {
      stroke = type === 'missing' ? '#4b5563' : '#1e293b';
      if (type === 'extracted') {
        stroke = '#4b5563';
      }
    }

    // In the basic odontogram, teeth with recommendations should appear with a
    // solid purple fill.
    if (isBasicOdontogram && status === 'recommended') {
      fill = '#6d28d9';
      stroke = '#1e293b';
    }

    return (
      <Suspense fallback={<Tooth6Missing className="vai-w-full vai-h-full" />}>
        <SvgComponent
          className="vai-text-blue-600"
          fill={fill}
          findingsFill={findingsFill}
          stroke={stroke}
          hx={hx}
          wx={wx}
        />
      </Suspense>
    );
  },
);
